/**
  Copyright Highway9 Networks Inc.
 */
import {
  PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
} from "@reduxjs/toolkit";
import { Clock, ClockPool } from "~/types/SyncProfile";
import { RootState } from "..";
import { clockPoolService, clockService } from "~/services/clockpool-service";
import { createSocketPatchReducer } from "../events/socketPatch";

type LoadingState = "idle" | "loading" | "succeeded" | "failed";

export const clockPoolsAdapter = createEntityAdapter<ClockPool>();
export const clocksAdapter = createEntityAdapter<Clock>({
  sortComparer: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
});

interface SyncProfileState {
  open: boolean;
  clockPoolsData: EntityState<ClockPool>;
  clockPoolsLoading: LoadingState;
  clockPoolsError: string | null;
  clocksData: EntityState<Clock>;
  clocksLoading: LoadingState;
  clocksError: string | null;
}

const initialState: SyncProfileState = {
  open: false,

  clockPoolsData: clockPoolsAdapter.getInitialState(),
  clockPoolsLoading: "idle",
  clockPoolsError: null,
  clocksData: clocksAdapter.getInitialState(),
  clocksLoading: "idle",
  clocksError: null,
};

export const fetchClockPools = createAsyncThunk(
  "syncProfile/fetchClockPools",
  async (_, { rejectWithValue }) => {
    try {
      const data = await clockPoolService.getAll();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch clock pools");
    }
  },
);

export const fetchClocks = createAsyncThunk(
  "syncProfile/fetchClocks",
  async (_, { rejectWithValue }) => {
    try {
      const data = await clockService.getAll();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch clocks");
    }
  },
);

const syncProfileSlice = createSlice({
  name: "syncProfile",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    clearClockPoolsError: (state) => {
      state.clockPoolsError = null;
    },
    clearClocksError: (state) => {
      state.clocksError = null;
    },
    applySocketPatchesClockPools: createSocketPatchReducer(clockPoolsAdapter, (s) => s.clockPoolsData),
    applySocketPatchesClocks: createSocketPatchReducer(clocksAdapter, (s) => s.clocksData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClockPools.pending, (state) => {
        state.clockPoolsLoading = "loading";
        state.clockPoolsError = null;
      })
      .addCase(fetchClockPools.fulfilled, (state, action) => {
        state.clockPoolsLoading = "succeeded";
        clockPoolsAdapter.setAll(state.clockPoolsData, action.payload);
      })
      .addCase(fetchClockPools.rejected, (state, action) => {
        state.clockPoolsLoading = "failed";
        state.clockPoolsError = action.payload as string;
      });

    builder
      .addCase(fetchClocks.pending, (state) => {
        state.clocksLoading = "loading";
        state.clocksError = null;
      })
      .addCase(fetchClocks.fulfilled, (state, action) => {
        state.clocksLoading = "succeeded";
        clocksAdapter.setAll(state.clocksData, action.payload);
      })
      .addCase(fetchClocks.rejected, (state, action) => {
        state.clocksLoading = "failed";
        state.clocksError = action.payload as string;
      });
  },
});

export const syncProfileActions = syncProfileSlice.actions;
export default syncProfileSlice.reducer;

const clockPoolEntitySelectors = clockPoolsAdapter.getSelectors(
  (state: RootState) => state.syncProfile.clockPoolsData,
);
const clockEntitySelectors = clocksAdapter.getSelectors(
  (state: RootState) => state.syncProfile.clocksData,
);

export const syncProfileOpen = (state: RootState) => state.syncProfile.open;

export const clockPoolData = clockPoolEntitySelectors.selectAll;
export const clockPoolEntities = clockPoolEntitySelectors.selectEntities;
export const clockPoolLoading = (state: RootState) => state.syncProfile.clockPoolsLoading;
export const clockPoolError = (state: RootState) => state.syncProfile.clockPoolsError;

export const clockData = clockEntitySelectors.selectAll;
export const clockEntities = clockEntitySelectors.selectEntities;
export const clockLoading = (state: RootState) => state.syncProfile.clocksLoading;
export const clockError = (state: RootState) => state.syncProfile.clocksError;

export const clocksByPoolId = (poolId: string) =>
  createSelector([clockData], (clocks) => clocks.filter((clock) => clock.clockPool?.id === poolId));
